/**
 * @file wxMpOAuth.js
 * @description 微信公众号网页 OAuth：10102 整页跳转、回调 token 落地
 * @module shared/utils/wxMpOAuth
 */

import { ERR_WX_MP_OAUTH_REQUIRED, normalizeApiCode } from '@/shared/utils/apiErrorCodes'
import { buildStoredSession, getAuthToken, saveAuthUser } from '@/shared/utils/auth'
import { getStoredUser } from '@/shared/utils/storage'

/** OAuth 整页跳转进行中（避免登录 reload 等打断授权） */
const OAUTH_IN_FLIGHT_KEY = 'comic:oauthInFlight'

/** OAuth 失败自动续打赏仅重试一次 */
const OAUTH_AUTO_RETRY_KEY = 'comic:pendingTipOAuthAutoRetried'

/** @typedef {'ok' | 'need-login'} ComicTipAuthResult */

/**
 * 从本地缓存读取当前登录用户 ID
 * @returns {string}
 */
export function getStoredUserId() {
  const user = getStoredUser()
  if (!user || typeof user !== 'object') return ''
  const raw = user.userId ?? user.id ?? user.userNo
  if (raw == null) return ''
  return String(raw).trim()
}

/**
 * 从本地缓存读取手机号（用于 OAuth 回站账号校验）
 * @returns {string}
 */
export function getStoredUserPhone() {
  const user = getStoredUser()
  if (!user || typeof user !== 'object') return ''
  const phone = user.phone
  return typeof phone === 'string' ? phone.trim() : ''
}

/**
 * 从 URL 中移除 token 参数，避免回跳或 authorize redirect 泄露
 * @param {string} [url]
 * @returns {string}
 */
export function stripTokenFromUrl(url) {
  if (typeof window === 'undefined') return url || ''
  try {
    const target = url || window.location.href
    const parsed = new URL(target)
    parsed.searchParams.delete('token')
    return parsed.toString()
  } catch {
    return url || ''
  }
}

/**
 * 当前页作为 OAuth 回跳地址（origin + pathname，去掉 token）
 * @returns {string}
 */
export function getOAuthRedirectPage() {
  if (typeof window === 'undefined') return ''
  return stripTokenFromUrl(`${window.location.origin}${window.location.pathname}`)
}

/**
 * OAuth authorize URL 是否含有效绑定参数（路径 bindTicket 或 query bind）
 * @param {string} url
 * @returns {boolean}
 */
export function hasValidOAuthBindParams(url) {
  if (!url?.trim()) return false
  if (url.includes('/oauth/authorize/b/')) return true
  try {
    const parsed = new URL(url.trim(), typeof window !== 'undefined' ? window.location.origin : undefined)
    const bindTicket = parsed.searchParams.get('bindTicket')?.trim()
    if (bindTicket) return true
    const bindUserId = parsed.searchParams.get('bindUserId')?.trim()
    const bindToken = parsed.searchParams.get('bindToken')?.trim()
    return Boolean(bindUserId && bindToken)
  } catch {
    return false
  }
}

/**
 * 若服务端 URL 缺 bind，用本地登录态补全 bindUserId+bindToken（兼容旧后端）
 * @param {string} url
 * @returns {string}
 */
export function enrichOAuthAuthorizeUrl(url) {
  const trimmed = typeof url === 'string' ? url.trim() : ''
  if (!trimmed || !trimmed.includes('/oauth/authorize')) return trimmed
  if (hasValidOAuthBindParams(trimmed)) return trimmed

  const token = getAuthToken()
  const userId = getStoredUserId()
  if (!token || !userId) return trimmed

  try {
    const parsed = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : undefined)
    if (!parsed.searchParams.get('redirect')?.trim()) {
      const page = getOAuthRedirectPage()
      if (page) parsed.searchParams.set('redirect', page)
    }
    parsed.searchParams.set('bindUserId', userId)
    parsed.searchParams.set('bindToken', token)
    return parsed.toString()
  } catch {
    return trimmed
  }
}

/**
 * 从 prepare 10102 响应解析可跳转的 OAuth URL
 * @param {unknown} body
 * @returns {string}
 */
export function resolveOAuthAuthorizeUrlForRedirect(body) {
  const data = body && typeof body === 'object' && 'data' in body ? body.data : null
  const rawUrl =
    data && typeof data === 'object' && 'oauthAuthorizeUrl' in data ? data.oauthAuthorizeUrl : ''
  const url = typeof rawUrl === 'string' ? rawUrl.trim() : ''
  if (!url || !url.includes('/oauth/authorize')) return ''
  const enriched = enrichOAuthAuthorizeUrl(url)
  return hasValidOAuthBindParams(enriched) ? enriched : ''
}

/**
 * App 入口同步吸收 OAuth 回跳 token，并 replaceState 清掉地址栏 token
 * @returns {boolean} 是否消费了 token
 */
export function absorbOAuthTokenFromUrl() {
  if (typeof window === 'undefined') return false
  try {
    const url = new URL(window.location.href)
    const token = url.searchParams.get('token')
    if (!token?.trim()) return false

    saveAuthUser(createSessionFromOAuthToken(token))
    clearOAuthInFlight()
    clearPendingTipOAuthAutoRetried()
    url.searchParams.delete('token')
    const next = `${url.pathname}${url.search}${url.hash}`
    window.history.replaceState(null, '', next)
    return true
  } catch {
    return false
  }
}

/**
 * 标记 OAuth 整页跳转已开始
 */
export function markOAuthInFlight() {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(OAUTH_IN_FLIGHT_KEY, '1')
}

/**
 * 清除 OAuth 进行中标记
 */
export function clearOAuthInFlight() {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(OAUTH_IN_FLIGHT_KEY)
}

/**
 * OAuth 整页跳转是否进行中
 * @returns {boolean}
 */
export function isOAuthInFlight() {
  if (typeof sessionStorage === 'undefined') return false
  return sessionStorage.getItem(OAUTH_IN_FLIGHT_KEY) === '1'
}

/**
 * 是否已对 OAuth 失败做过一次自动续打赏
 * @returns {boolean}
 */
export function hasPendingTipOAuthAutoRetried() {
  if (typeof sessionStorage === 'undefined') return false
  return sessionStorage.getItem(OAUTH_AUTO_RETRY_KEY) === '1'
}

/**
 * 标记 OAuth 失败后已自动续打赏一次
 */
export function markPendingTipOAuthAutoRetried() {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(OAUTH_AUTO_RETRY_KEY, '1')
}

/**
 * 清除 OAuth 自动续打赏重试标记
 */
export function clearPendingTipOAuthAutoRetried() {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(OAUTH_AUTO_RETRY_KEY)
}

/**
 * 消费 URL 中 oauthError 参数并清理地址栏
 * @param {import('vue-router').Router} router
 * @returns {string | null} oauthError 码；无则 null
 */
export function handleOAuthErrorFromRoute(router) {
  if (typeof window === 'undefined') return null
  const rawError = router.currentRoute.value.query?.oauthError
  const errorCode = Array.isArray(rawError) ? rawError[0] : rawError
  if (typeof errorCode !== 'string' || !errorCode.trim()) return null

  clearOAuthInFlight()
  const nextQuery = { ...router.currentRoute.value.query }
  delete nextQuery.oauthError
  delete nextQuery.oauthErrorMsg
  const path = router.currentRoute.value.path
  const search = new URLSearchParams()
  Object.entries(nextQuery).forEach(([key, value]) => {
    if (value == null) return
    if (Array.isArray(value)) {
      value.forEach((item) => search.append(key, String(item)))
    } else {
      search.set(key, String(value))
    }
  })
  const queryString = search.toString()
  const hash = router.currentRoute.value.hash || ''
  const nextUrl = `${path}${queryString ? `?${queryString}` : ''}${hash}`
  window.history.replaceState(null, '', nextUrl)
  void router.replace({ path, query: nextQuery, hash: router.currentRoute.value.hash })
  return errorCode.trim()
}

/**
 * prepare 返回 10102 时整页跳转 OAuth（须含 bindTicket 或 bindUserId+bindToken）
 * @param {unknown} body 接口响应体（含 code、data.oauthAuthorizeUrl）
 * @returns {boolean} 是否已发起整页跳转
 */
export function handleWxMpOAuthRequiredResponse(body) {
  if (typeof window === 'undefined') return false
  const code = normalizeApiCode(body && typeof body === 'object' && 'code' in body ? body.code : null)
  if (code !== ERR_WX_MP_OAUTH_REQUIRED) return false

  const targetUrl = resolveOAuthAuthorizeUrlForRedirect(body)
  if (!targetUrl) return false

  markOAuthInFlight()
  window.location.replace(targetUrl)
  return true
}

/**
 * 由 OAuth 回调 token 构造本地会话对象
 * @param {string} token
 * @returns {{ token: string, expiration: number }}
 */
export function createSessionFromOAuthToken(token) {
  return buildStoredSession({ token: token.trim() })
}

/**
 * 应用入口（无 router）清理 URL 中的 oauthError 参数
 * @returns {string | null} oauthError 码
 */
export function absorbOAuthErrorFromUrl() {
  if (typeof window === 'undefined') return null
  try {
    const url = new URL(window.location.href)
    const oauthError = url.searchParams.get('oauthError')?.trim()
    if (!oauthError) return null
    url.searchParams.delete('oauthError')
    url.searchParams.delete('oauthErrorMsg')
    const next = `${url.pathname}${url.search}${url.hash}`
    window.history.replaceState(null, '', next)
    return oauthError
  } catch {
    return null
  }
}

/**
 * 打赏前鉴权：须已登录（手机号/账号登录）；未登录弹登录窗，不走公众号 OAuth
 * @returns {ComicTipAuthResult}
 */
export function ensureAuthForComicTip() {
  return getAuthToken() ? 'ok' : 'need-login'
}

/**
 * 从路由 query 消费 OAuth 回调 token，并清除地址栏中的 token
 * @param {import('vue-router').Router} router
 * @returns {Promise<boolean>} 是否成功消费 token
 */
export async function consumeOAuthTokenFromRoute(router) {
  const absorbed = absorbOAuthTokenFromUrl()
  const raw = router.currentRoute.value.query?.token
  if (!absorbed && raw == null) return false

  if (!absorbed) {
    const token = Array.isArray(raw) ? raw[0] : raw
    if (typeof token !== 'string' || !token.trim()) return false
    saveAuthUser(createSessionFromOAuthToken(token))
  }

  if (router.currentRoute.value.query?.token != null) {
    const nextQuery = { ...router.currentRoute.value.query }
    delete nextQuery.token
    await router.replace({
      path: router.currentRoute.value.path,
      query: nextQuery,
      hash: router.currentRoute.value.hash,
    })
  }
  return true
}
